import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { useUserContext } from '../state/UserContext';
import { useClientSocket } from '../state/ClientSocketContext';
import { useApprovalEvents } from '../state/ApprovalEventsContext';
import type { SuppressedMentionAnnotation } from '../lib/mentions';
import type { ChatChangeReceipt } from '../client';

export interface VoiceLifecycleMarker {
  sessionId: string;
  event: 'started' | 'ended';
  occurredAt: string;
  agentId?: number;
  agentName?: string;
}

export interface ChatArtifactCreatedEvent {
  type: 'artifact_created';
  messageId: number;
  createdAt: string;
  agentId: number;
  artifact: {
    contentFileId: string;
    name: string;
    mediaType: 'video';
    availability: 'active' | 'expired' | 'deleted';
  };
}

/** Mirrors backend/src/chat-engine/types.ts wire protocol */
export interface ServerHistoryTurn {
  id: number;
  role: 'user' | 'assistant' | 'system';
  content: string;
  voiceTurnKey?: string;
  agentId?: number;
  agentName?: string;
  userId?: number;
  userName?: string;
  thoughts?: string;
  attachments?: { id: string; filename: string; mimeType: string }[];
  interaction?: ChatInteraction | null;
  status: 'complete' | 'error';
  createdAt: string;
  tokensSent?: number;
  tokensReceived?: number;
  costUsd?: number | null;
  mentions?: number[];
  suppressedMentions?: SuppressedMentionAnnotation[];
  voiceLifecycle?: VoiceLifecycleMarker;
  systemEvent?: ChatArtifactCreatedEvent;
  changeReceipts?: ChatChangeReceipt[];
}

/** A team member with access to the conversation */
export interface ChatParticipantView {
  userId: number;
  name: string;
  email?: string;
}

/** A field the user fills when granting a capability (mirrors the bundle def). */
export interface ChatInteractionField {
  key: string;
  label: string;
  placeholder?: string;
  secret?: boolean;
  /** Prefilled value the user can edit before granting (config fields) */
  value?: string;
}

/** Interactive prompt an agent posed (mirrors backend ChatMessageInteraction). */
export type ChatInteraction =
  | { kind: 'turn_retry_pending'; triggerMessageId: number; agentId: number }
  | {
      kind: 'question';
      options?: string[];
      allowOther?: boolean;
      status?: 'open' | 'answered';
    }
  | {
      kind: 'permission';
      bundleKey: string;
      title: string;
      description?: string;
      reason?: string;
      /** Markdown walkthrough (with links) on how to obtain the credentials */
      instructions?: string;
      fields?: ChatInteractionField[];
      status?: 'open' | 'granted' | 'declined';
      /** Display name of the team member who granted/declined. */
      resolvedBy?: string;
      approvalId?: number;
    }
  | {
      kind: 'resource_request';
      resourceType: 'workspace';
      token: string;
      revision: number;
      status:
        | 'pending'
        | 'approved'
        | 'rejected'
        | 'canceled'
        | 'provisioning'
        | 'fulfilled'
        | 'failed';
    }
  | {
      kind: 'monitor';
      monitorUuid: string;
      status: 'pending' | 'done' | 'timeout' | 'canceled' | 'error';
      checkIntervalSeconds: number;
      /** ISO timestamp of the monitor's max-duration deadline. */
      deadlineAt: string;
      /** Script-reported status line (MONITOR_STATUS marker), if any. */
      progressMessage?: string | null;
      /** Script-reported percent complete (MONITOR_PROGRESS marker, 0-100). */
      progressPercent?: number | null;
    }
  | {
      /** A durable pause. Mirrors the backend ChatMessageInteraction variant. */
      kind: 'deferred_wait';
      waitUuid: string;
      status:
        | 'pending'
        | 'completed'
        | 'max_wait_reached'
        | 'failed'
        | 'canceled';
      reason: string;
      waitKind: 'provider_operation' | 'timer' | 'workspace_script';
      deadlineAt: string;
      progressMessage?: string | null;
    }
  | {
      /** A form-assist surface was submitted and handed off to this chat */
      kind: 'form_submitted';
      formType: string;
      name: string;
      reference?: string;
    }
  | {
      kind: 'secret_create';
      proposedName: string;
      proposedDescription: string;
      reason: string;
      status: 'open' | 'granted' | 'declined';
      approvalId?: number;
      actualName?: string;
      resolvedBy?: string;
      decisionNote?: string;
    }
  | {
      kind: 'invite_user';
      email: string;
      reason?: string;
      status?: 'open' | 'granted' | 'declined';
      resolvedBy?: string;
      invitedEmail?: string;
      approvalId?: number;
    }
  | {
      /**
       * The platform asking an agent what should happen next on a card. It is
       * addressed to the agent, never to a person, so it carries no human
       * affordance.
       */
      kind: 'card_next_action_question';
      cardId: number;
    }
  | {
      /** The agent's reply: the follow-ups it recorded, or why there are none. */
      kind: 'card_next_action_answer';
      cardId: number;
      actionIds?: number[];
      nothingFurtherReason?: string;
    };

interface ServerEvent {
  type: string;
  [key: string]: any;
}

/** The forms that can carry an embedded form-assist chat. */
export type FormAssistFormType =
  | 'card'
  | 'epic'
  | 'sprint'
  | 'column'
  | 'project'
  | 'scheduled_task'
  | 'loop'
  | 'agent';

/** Form-assist surface options (chat embedded beside a form) */
export interface TeamChatSocketOptions {
  /**
   * Open a FRESH form_assist conversation bound to a board form; the
   * server narrows the roster to the team's system agent and gives it the
   * update_form tool. Omit for the ordinary team chat.
   */
  formAssist?: {
    formType: FormAssistFormType;
    /** Required by the board forms (card/epic/sprint/column) */
    projectId?: number;
    /** A scheduled-task form opened on one agent rather than the team */
    agentId?: number;
  };
  /** Live form patches from the agent's update_form tool */
  onFormPatch?: (patch: {
    formType: string;
    fields: Record<string, string>;
  }) => void;
}

/**
 * The machine-readable current-form block a form-assist send appends to
 * the user's text (and the renderer strips back out). The prompt tells the
 * agent the latest one is authoritative, so user edits always win.
 */
export function buildFormStateSuffix(state: Record<string, string>): string {
  return `\n\n<form_state>\n${JSON.stringify(state)}\n</form_state>`;
}

export function stripFormStateBlock(text: string): string {
  return text.replace(/\n*<form_state>[\s\S]*?<\/form_state>\s*$/, '');
}

export interface ToolActivityView {
  functionName: string;
  status: 'started' | 'succeeded' | 'failed';
}

/**
 * One entry in an agent's thinking stream, in stream order: either a run of
 * thought text, or a tool call. Lets the UI render tool pills inline where
 * they actually happened instead of grouping all tool calls above the
 * thinking text.
 */
export type ThinkingSegment =
  | { kind: 'thought'; text: string }
  | {
      kind: 'tool';
      functionName: string;
      status: ToolActivityView['status'];
      toolCallId: string;
      /** The tool's input params, streamed on the `started` frame */
      input?: unknown;
      /** The tool's result (or error), streamed on `succeeded`/`failed` */
      output?: unknown;
      /** Model-provided action_summary status line ("<agent> is <summary>…") */
      summary?: string;
      /** Client receipt time of the `started` frame (epoch ms), used to
       *  derive `durationMs` when the result frame lands */
      startedAt?: number;
      /** Wall-clock run time shown in the tool-call detail modal */
      durationMs?: number;
    };

export interface ChatAttachmentView {
  filename: string;
  mimeType: string;
  attachmentId?: string;
}

/** An attachment staged in the composer, uploading over the socket */
export interface PendingAttachment {
  clientId: string;
  filename: string;
  mimeType: string;
  status: 'uploading' | 'ready' | 'error';
  attachmentId?: string;
  error?: string;
}

export interface TeamChatAgent {
  id: number;
  name: string;
  /**
   * Shown greyed out in the @mention autocomplete; selecting it calls the
   * composer's onUnavailableMention instead of completing the mention
   * (e.g. a team agent not yet added to the project being commented on).
   */
  unavailable?: boolean;
}

/**
 * One rendered entry of the conversation. A user message and each agent
 * response are separate items: one message can trigger several agent
 * turns, and agents also respond to each other.
 */
export interface ChatItemView {
  key: string;
  kind: 'user' | 'agent' | 'divider' | 'notice';
  /** Stable provider turn identity for provisional voice reconciliation. */
  voiceTurnKey?: string;
  /** Persisted id of the user message / triggering message */
  messageId?: number;
  // --- user items ---
  userText?: string;
  attachments?: ChatAttachmentView[];
  /** sending -> done (acked/persisted); turn events arrive separately */
  sendStatus?: 'sending' | 'done';
  /** Authoring team member's name (multiplayer conversations) */
  authorName?: string;
  /** Authoring team member's id; ChatItem compares to the current user */
  authorUserId?: number;
  /** When the message was sent (ISO); shown as a relative "time ago" */
  timestamp?: string;
  // --- divider items ---
  dividerText?: string;
  /** Compaction dividers: what the summarize call(s) cost */
  dividerCostUsd?: number | null;
  /** Compaction dividers: the summary shown in the divider's modal */
  dividerSummary?: string;
  // --- notice items ---
  /** Short human-facing line for a 'notice' row (see formSubmittedNotice) */
  noticeText?: string;
  /** The created record's noun, name and reference, kept apart from the
   *  sentence so the row can link the NAME rather than a bare token. */
  noticeNoun?: string;
  noticeName?: string;
  noticeReference?: string;
  /** Durable voice-session boundary represented by this system notice. */
  voiceLifecycle?: VoiceLifecycleMarker;
  systemEvent?: ChatArtifactCreatedEvent;
  // --- agent items ---
  agentId?: number;
  agentName?: string;
  trigger?: 'user' | 'agent';
  triggeredByAgentName?: string;
  status?: 'processing' | 'complete' | 'error' | 'paused';
  /** Live-accumulated thinking tokens (folded provisional content included) */
  thinking: string;
  toolActivity: ToolActivityView[];
  /**
   * Ordered thought text + tool calls, interleaved in stream order (render
   * source for tool pills; `thinking`/`toolActivity` above are kept for
   * back-compat and reload text).
   */
  segments: ThinkingSegment[];
  /** Provisional streamed answer tokens for the current model turn */
  streamingContent: string;
  finalContent?: string;
  /** Names that remain visible after an invocation was declined or blocked. */
  suppressedMentions?: SuppressedMentionAnnotation[];
  /** Resolved agent identities addressed by this message. */
  mentionedAgentIds?: number[];
  errorMessage?: string;
  /** Interactive prompt on this agent message (question / permission request) */
  interaction?: ChatInteraction | null;
  /**
   * Persisted id of the message that carries the interaction (the agent
   * message's own id — messageId above is the *triggering* message for
   * live agent items). Needed to record grant/decline resolutions.
   */
  interactionMessageId?: number;
  /**
   * Persisted id of this agent message itself (set from message_complete
   * for live items, or from the history row's own id on reload). Used by
   * the tool-call detail modal to fetch this turn's tool calls.
   */
  assistantMessageId?: number;
  /** Token usage of the turn that produced this agent message */
  tokensSent?: number;
  tokensReceived?: number;
  /** Fully burdened USD cost of the turn (null/absent = unknown) */
  costUsd?: number | null;
  changeReceipts?: ChatChangeReceipt[];
}

export type ChatConnectionState =
  | 'connecting'
  | 'connected'
  | 'reconnecting'
  | 'disconnected';

type Action =
  | { type: 'reset'; items: ChatItemView[] }
  | { type: 'add_item'; item: ChatItemView }
  | {
      type: 'ack';
      clientMessageId: string;
      messageId: number;
    }
  | {
      type: 'user_message_posted';
      messageId: number;
      content: string;
      attachments?: ChatAttachmentView[];
      authorName: string;
      authorUserId?: number;
      createdAt?: string;
      mentions?: number[];
      suppressedMentions?: SuppressedMentionAnnotation[];
      voiceTurnKey?: string;
    }
  | {
      type: 'system_message_posted';
      pauseOrigin?: { triggerMessageId: number; agentId: number };
      messageId: number;
      content: string;
      interaction?: ChatInteraction;
      voiceLifecycle?: VoiceLifecycleMarker;
      systemEvent?: ChatArtifactCreatedEvent;
      agentName?: string;
      createdAt?: string;
      changeReceipts?: ChatChangeReceipt[];
    }
  | {
      type: 'turn_started';
      messageId: number;
      agentId: number;
      agentName: string;
      trigger: 'user' | 'agent';
      triggeredByAgentName?: string;
      voiceTurnKey?: string;
    }
  | {
      type: 'thinking_delta';
      messageId: number;
      agentId: number;
      delta: string;
    }
  | { type: 'message_delta'; messageId: number; agentId: number; delta: string }
  | {
      type: 'tool_activity';
      messageId: number;
      agentId: number;
      functionName: string;
      status: ToolActivityView['status'];
      toolCallId: string;
      input?: unknown;
      output?: unknown;
      summary?: string;
      changeReceipts?: ChatChangeReceipt[];
    }
  | {
      type: 'message_complete';
      messageId: number;
      agentId: number;
      agentName?: string;
      /** The agent message's own persisted id */
      assistantMessageId?: number;
      content: string;
      thoughts: string;
      /** Images the agent generated during this turn. */
      attachments?: ChatAttachmentView[];
      interaction?: ChatInteraction | null;
      tokensSent?: number;
      tokensReceived?: number;
      costUsd?: number | null;
      mentions?: { id: number; name: string }[];
      suppressedMentions?: SuppressedMentionAnnotation[];
      voiceTurnKey?: string;
      changeReceipts?: ChatChangeReceipt[];
    }
  | { type: 'turn_error'; messageId: number; message: string }
  | { type: 'turn_retry_pending'; messageId: number }
  | { type: 'turn_paused'; messageId: number; message: string }
  | { type: 'reject_sending' };

const agentKey = (messageId: number, agentId: number) =>
  `turn-${messageId}-${agentId}`;

function updateItem(
  items: ChatItemView[],
  predicate: (item: ChatItemView) => boolean,
  update: (item: ChatItemView) => ChatItemView,
): ChatItemView[] {
  return items.map((item) => (predicate(item) ? update(item) : item));
}

function byAgentTurn(messageId: number, agentId: number) {
  return (item: ChatItemView) =>
    item.kind === 'agent' &&
    item.messageId === messageId &&
    item.agentId === agentId &&
    item.status === 'processing';
}

/** Reconciles replayed receipt snapshots without losing earlier successes. */
export function mergeChangeReceipts(
  current: ChatChangeReceipt[] | undefined,
  incoming: ChatChangeReceipt[] | undefined,
): ChatChangeReceipt[] | undefined {
  if (!incoming?.length) return current;
  const merged = [...(current || [])];
  const positions = new Map(
    merged.map((receipt, index) => [receipt.id, index]),
  );
  for (const receipt of incoming) {
    const existingIndex = positions.get(receipt.id);
    if (existingIndex === undefined) {
      positions.set(receipt.id, merged.length);
      merged.push(receipt);
      continue;
    }
    const existing = merged[existingIndex];
    merged[existingIndex] = {
      ...receipt,
      action:
        existing.action === 'created' || receipt.action === 'created'
          ? 'created'
          : 'edited',
    };
  }
  return merged;
}

/**
 * Appends thought text to a segment list, merging into the trailing
 * segment when it is itself a thought (deltas stream in small chunks and
 * should read as one continuous run of text, not one segment per delta).
 */
function appendThought(
  segments: ThinkingSegment[],
  text: string,
): ThinkingSegment[] {
  const last = segments[segments.length - 1];
  if (last?.kind === 'thought') {
    return [
      ...segments.slice(0, -1),
      { kind: 'thought', text: last.text + text },
    ];
  }
  return [...segments, { kind: 'thought', text }];
}

/** Nouns for the hand-off notice, mirroring the server's FORM_ASSIST_NOUNS. */
const FORM_SUBMITTED_NOUNS: Record<string, string> = {
  card: 'Card',
  epic: 'Epic',
  sprint: 'Sprint',
  column: 'Column',
  project: 'Project',
  scheduled_task: 'Scheduled task',
  loop: 'Loop',
  agent: 'Agent',
};

/**
 * A form-assist hand-off row. The stored message body is written FOR THE
 * MODEL — it ends with instructions about continuing — so the transcript
 * renders a short line built from the interaction payload instead.
 */
function formSubmittedNotice(message: {
  id: number;
  interaction: { formType?: string; name?: string; reference?: string };
  createdAt?: string;
}): ChatItemView {
  const noun =
    FORM_SUBMITTED_NOUNS[message.interaction.formType || ''] || 'Item';
  const reference = message.interaction.reference
    ? ` (${message.interaction.reference})`
    : '';
  return {
    key: `notice-${message.id}`,
    kind: 'notice',
    messageId: message.id,
    noticeText: `${noun} “${message.interaction.name || ''}”${reference} was created.`,
    noticeNoun: noun,
    noticeName: message.interaction.name || '',
    noticeReference: message.interaction.reference || '',
    timestamp: message.createdAt,
    thinking: '',
    toolActivity: [],
    segments: [],
    streamingContent: '',
  };
}

function voiceLifecycleNotice(message: {
  id: number;
  content: string;
  voiceLifecycle: VoiceLifecycleMarker;
}): ChatItemView & {
  messageId: number;
  voiceLifecycle: VoiceLifecycleMarker;
  timestamp: string;
} {
  return {
    key: `voice-lifecycle-${message.id}`,
    kind: 'notice',
    messageId: message.id,
    noticeText: message.content,
    voiceLifecycle: message.voiceLifecycle,
    timestamp: message.voiceLifecycle.occurredAt,
    thinking: '',
    toolActivity: [],
    segments: [],
    streamingContent: '',
  };
}

function insertChronologicalNotice(
  items: ChatItemView[],
  notice: ChatItemView & {
    messageId: number;
  },
): ChatItemView[] {
  const occurredAt = Date.parse(notice.timestamp || '');
  if (!Number.isFinite(occurredAt)) return [...items, notice];
  const insertionIndex = items.findIndex((item) => {
    if (!item.timestamp) return false;
    const itemOccurredAt = Date.parse(item.timestamp);
    if (!Number.isFinite(itemOccurredAt)) return false;
    if (itemOccurredAt !== occurredAt) return itemOccurredAt > occurredAt;
    return item.messageId !== undefined && item.messageId > notice.messageId;
  });
  if (insertionIndex === -1) return [...items, notice];
  return [
    ...items.slice(0, insertionIndex),
    notice,
    ...items.slice(insertionIndex),
  ];
}

function systemInteractionItem(message: {
  id: number;
  content: string;
  interaction: ChatInteraction;
  createdAt?: string;
  changeReceipts?: ChatChangeReceipt[];
}): ChatItemView {
  if (message.interaction.kind === 'turn_retry_pending') {
    return {
      key: `system-retry-${message.id}`,
      kind: 'notice',
      messageId: message.id,
      noticeText: message.content,
      timestamp: message.createdAt,
      interaction: message.interaction,
      thinking: '',
      toolActivity: [],
      segments: [],
      streamingContent: '',
      changeReceipts: message.changeReceipts,
    };
  }
  if (message.interaction.kind === 'form_submitted') {
    return formSubmittedNotice({
      id: message.id,
      interaction: message.interaction,
      createdAt: message.createdAt,
    });
  }
  return {
    key: `system-interaction-${message.id}`,
    kind: 'agent',
    messageId: message.id,
    agentName: 'Workspace',
    status: 'complete',
    timestamp: message.createdAt,
    thinking: '',
    toolActivity: [],
    segments: [],
    streamingContent: '',
    finalContent: message.content,
    interaction: message.interaction,
    interactionMessageId: message.id,
    changeReceipts: message.changeReceipts,
  };
}

/**
 * Legacy conversations posted one resource_request system message per
 * lifecycle status (pending/fulfilled/…) before workspace approval status
 * updates started applying in place (see Task 4). The approval card
 * fetches live state by token, so only the first message per token needs
 * to keep its interaction — later ones (legacy duplicates, or a same-token
 * race) render as a plain message with no card.
 */
function hasResourceRequestForToken(
  items: ChatItemView[],
  token: string | undefined,
): boolean {
  if (!token) return false;
  return items.some(
    (item) =>
      item.interaction?.kind === 'resource_request' &&
      item.interaction.token === token,
  );
}

export function chatItemsReducer(
  items: ChatItemView[],
  action: Action,
): ChatItemView[] {
  switch (action.type) {
    case 'reset':
      return action.items;
    case 'add_item':
      return [...items, action.item];
    case 'system_message_posted': {
      const pauseOrigin = action.pauseOrigin;
      if (pauseOrigin && action.changeReceipts?.length) {
        const deliveredIds = new Set(
          action.changeReceipts.map((receipt) => receipt.id),
        );
        items = items.map((item) =>
          item.kind === 'agent' &&
          item.messageId === pauseOrigin.triggerMessageId &&
          item.agentId === pauseOrigin.agentId
            ? {
                ...item,
                changeReceipts: item.changeReceipts?.filter(
                  (receipt) => !deliveredIds.has(receipt.id),
                ),
              }
            : item,
        );
      }
      // A re-publish for a message that is already rendered means its
      // interaction changed in place (e.g. a monitor card flipping from
      // pending to done/timeout/canceled) — update it instead of duplicating.
      const existing = items.find(
        (item) =>
          item.messageId === action.messageId ||
          item.interactionMessageId === action.messageId,
      );
      if (existing) {
        return updateItem(
          items,
          (item) => item === existing,
          (item) => ({
            ...item,
            interaction: action.interaction ?? item.interaction,
            changeReceipts: mergeChangeReceipts(
              item.changeReceipts,
              action.changeReceipts,
            ),
            ...(action.interaction?.kind === 'turn_retry_pending'
              ? { noticeText: action.content }
              : {}),
          }),
        );
      }
      if (!action.interaction) {
        if (action.systemEvent) {
          return insertChronologicalNotice(items, {
            key: `artifact-${action.messageId}`,
            kind: 'notice',
            messageId: action.messageId,
            agentName: action.agentName,
            systemEvent: action.systemEvent,
            timestamp: action.createdAt,
            thinking: '',
            toolActivity: [],
            segments: [],
            streamingContent: '',
            changeReceipts: action.changeReceipts,
          });
        }
        if (action.voiceLifecycle) {
          return insertChronologicalNotice(
            items,
            voiceLifecycleNotice({
              id: action.messageId,
              content: action.content,
              voiceLifecycle: action.voiceLifecycle,
            }),
          );
        }
        return [
          ...items,
          {
            key: `system-message-${action.messageId}`,
            kind: 'divider',
            messageId: action.messageId,
            dividerText: action.content || 'Conversation update',
            timestamp: action.createdAt,
            thinking: '',
            toolActivity: [],
            segments: [],
            streamingContent: '',
            changeReceipts: action.changeReceipts,
          },
        ];
      }
      const next = systemInteractionItem({
        id: action.messageId,
        content: action.content,
        interaction: action.interaction,
        createdAt: action.createdAt,
        changeReceipts: action.changeReceipts,
      });
      if (
        next.interaction?.kind === 'resource_request' &&
        hasResourceRequestForToken(items, next.interaction.token)
      ) {
        next.interaction = undefined;
        next.interactionMessageId = undefined;
      }
      return [...items, next];
    }
    case 'ack':
      // The ack only means "persisted and visible to everyone"; the turn
      // events for the message follow separately
      return updateItem(
        items,
        (item) => item.key === action.clientMessageId,
        (item) => ({
          ...item,
          messageId: action.messageId,
          sendStatus: 'done',
        }),
      );
    case 'user_message_posted': {
      // Own message (already reconciled via the ack): just annotate it.
      // Another participant's message: append a new user item.
      let ownIndex = items.findIndex(
        (item) => item.kind === 'user' && item.messageId === action.messageId,
      );
      // Fallback if this broadcast beat the ack: reconcile the still-
      // sending optimistic bubble by content so it is never duplicated.
      if (ownIndex === -1) {
        ownIndex = items.findIndex(
          (item) =>
            item.kind === 'user' &&
            item.sendStatus === 'sending' &&
            item.messageId === undefined &&
            item.userText === action.content,
        );
      }
      if (ownIndex !== -1) {
        return items.map((item, index) =>
          index === ownIndex
            ? {
                ...item,
                messageId: action.messageId,
                sendStatus: 'done',
                authorName: action.authorName,
                timestamp: action.createdAt ?? item.timestamp,
                mentionedAgentIds: action.mentions?.map(Number),
                suppressedMentions: action.suppressedMentions,
                voiceTurnKey: action.voiceTurnKey ?? item.voiceTurnKey,
              }
            : item,
        );
      }
      return [
        ...items,
        {
          key: `posted-${action.messageId}`,
          kind: 'user',
          messageId: action.messageId,
          userText: action.content,
          attachments: action.attachments,
          authorName: action.authorName,
          authorUserId: action.authorUserId,
          timestamp: action.createdAt,
          mentionedAgentIds: action.mentions?.map(Number),
          suppressedMentions: action.suppressedMentions,
          voiceTurnKey: action.voiceTurnKey,
          sendStatus: 'done',
          thinking: '',
          toolActivity: [],
          segments: [],
          streamingContent: '',
        },
      ];
    }
    case 'turn_started': {
      // The user item stops showing its pending state once an agent turn
      // for it begins
      const settled = updateItem(
        items,
        (item) =>
          item.kind === 'user' &&
          item.messageId === action.messageId &&
          item.sendStatus !== 'done',
        (item) => ({ ...item, sendStatus: 'done' }),
      );
      return [
        ...settled,
        {
          key: `${agentKey(action.messageId, action.agentId)}-${items.length}`,
          kind: 'agent',
          messageId: action.messageId,
          agentId: action.agentId,
          agentName: action.agentName,
          voiceTurnKey: action.voiceTurnKey,
          trigger: action.trigger,
          triggeredByAgentName: action.triggeredByAgentName,
          status: 'processing',
          timestamp: new Date().toISOString(),
          thinking: '',
          toolActivity: [],
          segments: [],
          streamingContent: '',
        },
      ];
    }
    case 'thinking_delta':
      return updateItem(
        items,
        byAgentTurn(action.messageId, action.agentId),
        (item) => ({
          ...item,
          thinking: item.thinking + action.delta,
          segments: appendThought(item.segments, action.delta),
        }),
      );
    case 'message_delta':
      return updateItem(
        items,
        byAgentTurn(action.messageId, action.agentId),
        (item) => ({
          ...item,
          streamingContent: item.streamingContent + action.delta,
        }),
      );
    case 'tool_activity':
      return updateItem(
        items,
        byAgentTurn(action.messageId, action.agentId),
        (item) => {
          // A tool call means the streamed content so far was agent-loop
          // reasoning, not the final answer: fold it into the thinking view
          const folded =
            action.status === 'started' && item.streamingContent
              ? item.thinking +
                (item.thinking ? '\n\n' : '') +
                item.streamingContent
              : item.thinking;

          const existingIndex = item.toolActivity.findIndex(
            (activity) =>
              activity.functionName === action.functionName &&
              activity.status === 'started',
          );

          let toolActivity: ToolActivityView[];
          if (action.status === 'started' || existingIndex === -1) {
            toolActivity = [
              ...item.toolActivity,
              { functionName: action.functionName, status: action.status },
            ];
          } else {
            toolActivity = item.toolActivity.map((activity, index) =>
              index === existingIndex
                ? { ...activity, status: action.status }
                : activity,
            );
          }

          // Segments mirror the same fold-then-append/update behavior, but
          // keyed by toolCallId (stable across started -> succeeded/failed)
          // instead of by functionName + "first started" lookup. The folded
          // streamingContent is pushed as its OWN thought segment (not merged
          // into a trailing thought via appendThought): it came from a
          // separate stream and must render as a distinct block, matching the
          // '\n\n' paragraph break the thinking string gets above.
          let segments =
            action.status === 'started' && item.streamingContent
              ? [
                  ...item.segments,
                  { kind: 'thought' as const, text: item.streamingContent },
                ]
              : item.segments;
          if (action.status === 'started') {
            segments = [
              ...segments,
              {
                kind: 'tool',
                functionName: action.functionName,
                status: action.status,
                toolCallId: action.toolCallId,
                input: action.input,
                summary: action.summary,
                startedAt: Date.now(),
              },
            ];
          } else {
            segments = segments.map((segment) =>
              segment.kind === 'tool' &&
              segment.toolCallId === action.toolCallId
                ? {
                    ...segment,
                    status: action.status,
                    output: action.output,
                    // Client-side receipt-to-receipt timing: both frames ride
                    // the same socket, so transit latency mostly cancels out
                    durationMs:
                      segment.startedAt !== undefined
                        ? Date.now() - segment.startedAt
                        : undefined,
                  }
                : segment,
            );
          }

          return {
            ...item,
            thinking: folded,
            streamingContent:
              action.status === 'started' ? '' : item.streamingContent,
            toolActivity,
            segments,
            changeReceipts: mergeChangeReceipts(
              item.changeReceipts,
              action.changeReceipts,
            ),
          };
        },
      );
    case 'message_complete': {
      const processingIndex = items.findIndex(
        byAgentTurn(action.messageId, action.agentId),
      );
      const completedIndex = items.findIndex(
        (item) =>
          item.kind === 'agent' &&
          ((action.assistantMessageId !== undefined &&
            item.assistantMessageId === action.assistantMessageId) ||
            (item.messageId === action.messageId &&
              item.agentId === action.agentId &&
              item.status === 'complete')),
      );
      const existingIndex =
        processingIndex !== -1 ? processingIndex : completedIndex;
      const completedItem = (item: ChatItemView): ChatItemView => ({
        ...item,
        agentName: action.agentName ?? item.agentName,
        status: 'complete',
        finalContent: action.content,
        attachments: action.attachments ?? item.attachments,
        thinking: action.thoughts || item.thinking,
        streamingContent: '',
        interaction: action.interaction ?? item.interaction,
        interactionMessageId: action.assistantMessageId,
        assistantMessageId:
          action.assistantMessageId ?? item.assistantMessageId,
        tokensSent: action.tokensSent,
        tokensReceived: action.tokensReceived,
        costUsd: action.costUsd,
        mentionedAgentIds: action.mentions?.map((mention) =>
          Number(mention.id),
        ),
        suppressedMentions: action.suppressedMentions,
        voiceTurnKey: action.voiceTurnKey ?? item.voiceTurnKey,
        changeReceipts: mergeChangeReceipts(
          item.changeReceipts,
          action.changeReceipts,
        ),
      });
      if (existingIndex !== -1) {
        return items.map((item, index) =>
          index === existingIndex ? completedItem(item) : item,
        );
      }
      return [
        ...items,
        completedItem({
          key: `completed-${action.assistantMessageId ?? `${action.messageId}-${action.agentId}`}`,
          kind: 'agent',
          messageId: action.messageId,
          agentId: action.agentId,
          agentName: action.agentName,
          thinking: '',
          toolActivity: [],
          segments: [],
          streamingContent: '',
        }),
      ];
    }
    case 'turn_retry_pending':
      return items.filter(
        (item) =>
          !(
            item.kind === 'agent' &&
            item.messageId === action.messageId &&
            item.status === 'processing'
          ),
      );
    case 'turn_error': {
      // The error event carries no agentId: it lands on the processing
      // agent item for the message, or on the user item pre-turn
      const processingIndex = items.findIndex(
        (item) =>
          item.kind === 'agent' &&
          item.messageId === action.messageId &&
          item.status === 'processing',
      );
      if (processingIndex !== -1) {
        return items.map((item, index) =>
          index === processingIndex
            ? {
                ...item,
                status: 'error',
                errorMessage: action.message,
                streamingContent: '',
              }
            : item,
        );
      }
      return updateItem(
        items,
        (item) => item.kind === 'user' && item.messageId === action.messageId,
        (item) => ({ ...item, sendStatus: 'done' }),
      );
    }
    case 'turn_paused': {
      // A pause is not an error: end the in-flight turn with a subdued
      // "paused" notice rather than a red error. (On reload the persisted
      // "⏸️ … paused" system message — which carries the same links — takes
      // over; the paused turn itself is never persisted as an agent message.)
      const processingIndex = items.findIndex(
        (item) =>
          item.kind === 'agent' &&
          item.messageId === action.messageId &&
          item.status === 'processing',
      );
      if (processingIndex !== -1) {
        return items.map((item, index) =>
          index === processingIndex
            ? {
                ...item,
                status: 'paused',
                errorMessage: action.message,
                streamingContent: '',
              }
            : item,
        );
      }
      return updateItem(
        items,
        (item) => item.kind === 'user' && item.messageId === action.messageId,
        (item) => ({ ...item, sendStatus: 'done' }),
      );
    }
    case 'reject_sending':
      // The server rejected the message before persisting it (e.g. no
      // @mention): drop the optimistic bubble instead of leaving it
      // "Sending…" forever
      return items.filter(
        (item) => !(item.kind === 'user' && item.sendStatus === 'sending'),
      );
    default:
      return items;
  }
}

/**
 * Builds a system-interaction history item, stripping the interaction when
 * its resource_request token already appeared earlier in the history (see
 * hasResourceRequestForToken above — same dedup, threaded via a running set
 * of tokens instead of re-scanning the mapped items each time).
 */
function historySystemInteractionItem(
  message: ServerHistoryTurn,
  seenResourceRequestTokens: Set<string>,
): ChatItemView {
  const item = systemInteractionItem({
    id: message.id,
    content: message.content,
    interaction: message.interaction!,
    createdAt: message.createdAt,
    changeReceipts: message.changeReceipts,
  });
  if (item.interaction?.kind === 'resource_request') {
    if (seenResourceRequestTokens.has(item.interaction.token)) {
      item.interaction = undefined;
      item.interactionMessageId = undefined;
    } else {
      seenResourceRequestTokens.add(item.interaction.token);
    }
  }
  return item;
}

export function historyToItems(history: ServerHistoryTurn[]): ChatItemView[] {
  // Legacy conversations can carry several system messages for the same
  // resource_request token (one per lifecycle status); track which tokens
  // have already been represented as we map in order so only the first
  // keeps its interaction.
  const seenResourceRequestTokens = new Set<string>();
  return history.map((message) =>
    message.role === 'system' && message.systemEvent
      ? {
          key: `artifact-${message.id}`,
          kind: 'notice' as const,
          messageId: message.id,
          agentName: message.agentName,
          systemEvent: message.systemEvent,
          timestamp: message.createdAt,
          thinking: '',
          toolActivity: [],
          segments: [],
          streamingContent: '',
          changeReceipts: message.changeReceipts,
        }
      : message.role === 'system' && message.interaction
        ? historySystemInteractionItem(message, seenResourceRequestTokens)
        : message.role === 'system' && message.voiceLifecycle
          ? voiceLifecycleNotice({
              id: message.id,
              content: message.content,
              voiceLifecycle: message.voiceLifecycle,
            })
          : message.role === 'system'
            ? {
                key: `history-${message.id}`,
                kind: 'divider' as const,
                messageId: message.id,
                dividerText: message.content || 'Conversation compacted',
                // Compaction markers carry their summary in the row's
                // thoughts and their summarize cost via the metric join
                dividerSummary: message.thoughts || undefined,
                dividerCostUsd: message.costUsd,
                thinking: '',
                toolActivity: [],
                segments: [],
                streamingContent: '',
                changeReceipts: message.changeReceipts,
              }
            : message.role === 'user'
              ? {
                  key: `history-${message.id}`,
                  kind: 'user' as const,
                  messageId: message.id,
                  // The form-assist trailer is a machine artifact of the message,
                  // never something to show a reader — and a handed-off
                  // conversation replays through the dock, whose socket knows
                  // nothing about form assist, so this cannot be gated on that.
                  userText: stripFormStateBlock(message.content || ''),
                  voiceTurnKey: message.voiceTurnKey,
                  authorName: message.userName,
                  authorUserId:
                    message.userId !== undefined
                      ? Number(message.userId)
                      : undefined,
                  timestamp: message.createdAt,
                  attachments: message.attachments?.map((attachment) => ({
                    filename: attachment.filename,
                    mimeType: attachment.mimeType,
                    attachmentId: attachment.id,
                  })),
                  sendStatus: 'done' as const,
                  mentionedAgentIds: message.mentions?.map(Number),
                  suppressedMentions: message.suppressedMentions,
                  thinking: '',
                  toolActivity: [],
                  segments: [],
                  streamingContent: '',
                }
              : {
                  key: `history-${message.id}`,
                  kind: 'agent' as const,
                  messageId: message.id,
                  agentId: message.agentId,
                  agentName: message.agentName,
                  voiceTurnKey: message.voiceTurnKey,
                  timestamp: message.createdAt,
                  // Images the agent generated during the turn.
                  attachments: message.attachments?.map((attachment) => ({
                    filename: attachment.filename,
                    mimeType: attachment.mimeType,
                    attachmentId: attachment.id,
                  })),
                  status:
                    message.status === 'error'
                      ? ('error' as const)
                      : ('complete' as const),
                  thinking: message.thoughts || '',
                  toolActivity: [],
                  segments: message.thoughts
                    ? [{ kind: 'thought' as const, text: message.thoughts }]
                    : [],
                  streamingContent: '',
                  finalContent:
                    message.status === 'error' ? undefined : message.content,
                  mentionedAgentIds: message.mentions?.map(Number),
                  suppressedMentions: message.suppressedMentions,
                  errorMessage:
                    message.status === 'error' ? message.content : undefined,
                  interaction: message.interaction ?? undefined,
                  interactionMessageId: message.interaction
                    ? message.id
                    : undefined,
                  assistantMessageId: message.id,
                  tokensSent: message.tokensSent,
                  tokensReceived: message.tokensReceived,
                  costUsd: message.costUsd,
                  changeReceipts: message.changeReceipts,
                },
  );
}

function genId(): string {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `id-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export interface ApprovalState {
  exchangeCount: number;
  pendingAgentNames: string[];
  approvalId?: number;
}

/**
 * WebSocket client for the unified team chat. Manages connection lifecycle
 * with automatic reconnect, and reduces server events into renderable
 * conversation items (user messages and per-agent responses).
 */
interface EnsureConversationDeferred {
  promise: Promise<number>;
  resolve: (conversationId: number) => void;
  reject: (error: Error) => void;
}

function rejectPendingEnsureConversation(
  ref: { current: EnsureConversationDeferred | null },
  message: string,
): void {
  const pending = ref.current;
  if (!pending) return;
  ref.current = null;
  pending.reject(new Error(message));
}

export function useTeamChatSocket(
  teamId: string | undefined,
  options?: TeamChatSocketOptions,
) {
  const [items, dispatch] = useReducer(chatItemsReducer, []);
  const [connectionState, setConnectionState] =
    useState<ChatConnectionState>('connecting');
  const [conversationId, setConversationId] = useState<number | null>(null);
  const [conversationGeneration, setConversationGeneration] = useState(0);
  const [conversationTitle, setConversationTitle] = useState<string | null>(
    null,
  );
  const [agents, setAgents] = useState<TeamChatAgent[]>([]);
  const [participants, setParticipants] = useState<ChatParticipantView[]>([]);
  const [approval, setApproval] = useState<ApprovalState | null>(null);
  const [globalError, setGlobalError] = useState<string | null>(null);
  const [pendingAttachments, setPendingAttachments] = useState<
    PendingAttachment[]
  >([]);

  const { user } = useUserContext();
  const { send, subscribe, connected, epoch } = useClientSocket();
  const { epoch: approvalEpoch, changedApprovalId } = useApprovalEvents();
  // Read through a ref so the stable handleServerEvent/openChannel callbacks
  // always see the caller's latest options without re-subscribing.
  const optionsRef = useRef<TeamChatSocketOptions | undefined>(options);
  optionsRef.current = options;
  const conversationIdRef = useRef<number | null>(null);
  const ensureConversationRef = useRef<EnsureConversationDeferred | null>(null);
  /** This chat channel's open-request id; frames tagged with it are ours */
  const requestIdRef = useRef<string | null>(null);
  /**
   * True once the server has acked `opened` for the current requestId. A
   * lazy (unsaved) channel is open with no conversationId yet, so sends are
   * gated on this rather than on conversationIdRef — sending before this is
   * true would race the server's own open/close handling.
   */
  const channelOpenRef = useRef<boolean>(false);
  /** What we want open on this connection: a conversation, or a fresh one */
  const desiredRef = useRef<{
    conversationId: number | null;
    forceNew: boolean;
    /** Origin page for a new highlight-to-chat conversation */
    wikiScope?: { projectId: number; pagePath: string };
    // Form-assist chats start fresh per mount: a stale conversation would
    // carry the previous form's context into a new one.
  }>({ conversationId: null, forceNew: !!options?.formAssist });

  const handleServerEvent = useCallback((event: ServerEvent) => {
    switch (event.type) {
      case 'ready':
        console.log(
          `[chat] ready: conversation ${event.conversationId}, ${(event.history || []).length} history entries`,
        );
        conversationIdRef.current = event.conversationId;
        setConversationId(event.conversationId);
        setConversationTitle(event.title ?? null);
        setAgents(event.agents || []);
        setParticipants(event.participants || []);
        setApproval(
          event.awaitingApproval
            ? {
                exchangeCount: 0,
                pendingAgentNames: [],
                approvalId: event.approvalId,
              }
            : null,
        );
        dispatch({
          type: 'reset',
          items: historyToItems(event.history || []),
        });
        // A brand-new conversation's opening line: rendered as an ordinary
        // first agent bubble. The server persists the same text as a real
        // assistant message when the lazy row is created, so it only rides
        // the ready snapshot while history is empty.
        if (event.opener && (event.history || []).length === 0) {
          dispatch({
            type: 'add_item',
            item: {
              key: 'opener',
              kind: 'agent',
              agentName: event.opener.agentName,
              status: 'complete',
              thinking: '',
              toolActivity: [],
              segments: [],
              streamingContent: '',
              finalContent: event.opener.content,
            },
          });
        }
        // Only now is the conversation usable: 'ready' resets the item
        // list, so enabling the composer on socket-open would let a
        // message be sent and then wiped by a late-arriving ready
        setConnectionState('connected');
        break;
      case 'conversation_created':
        // The server just created the row for this previously-unsaved
        // channel; adopt its real id (frames route by it from now on).
        conversationIdRef.current = event.conversationId;
        desiredRef.current.conversationId = event.conversationId;
        setConversationId(event.conversationId);
        if (ensureConversationRef.current) {
          const pending = ensureConversationRef.current;
          ensureConversationRef.current = null;
          pending.resolve(event.conversationId);
        }
        break;
      case 'ack':
        dispatch({
          type: 'ack',
          clientMessageId: event.clientMessageId,
          messageId: event.messageId,
        });
        break;
      case 'user_message_posted':
        dispatch({
          type: 'user_message_posted',
          messageId: event.messageId,
          content: stripFormStateBlock(event.content || ''),
          attachments: event.attachments?.map(
            (attachment: {
              id: string;
              filename: string;
              mimeType: string;
            }) => ({
              filename: attachment.filename,
              mimeType: attachment.mimeType,
              attachmentId: attachment.id,
            }),
          ),
          authorName: event.author?.name || 'Team member',
          authorUserId:
            event.author?.userId !== undefined
              ? Number(event.author.userId)
              : undefined,
          createdAt: event.createdAt,
          mentions: event.mentions,
          suppressedMentions: event.suppressedMentions,
          voiceTurnKey: event.voiceTurnKey,
        });
        break;
      case 'system_message_posted':
        dispatch({
          type: 'system_message_posted',
          pauseOrigin: event.pauseOrigin,
          messageId: event.messageId,
          content: event.content,
          interaction: event.interaction,
          voiceLifecycle: event.voiceLifecycle,
          systemEvent: event.systemEvent,
          agentName: event.agentName,
          createdAt: event.createdAt,
          changeReceipts: event.changeReceipts,
        });
        break;
      case 'participant_added':
        setParticipants((current) =>
          current.some((participant) => participant.userId === event.userId)
            ? current
            : [...current, { userId: event.userId, name: event.name }],
        );
        break;
      case 'conversation_compacted':
        dispatch({
          type: 'add_item',
          item: {
            key: `compacted-${event.markerMessageId}`,
            kind: 'divider',
            messageId: event.markerMessageId,
            dividerText: 'Conversation compacted',
            dividerSummary: event.summary || undefined,
            dividerCostUsd: event.costUsd,
            thinking: '',
            toolActivity: [],
            segments: [],
            streamingContent: '',
          },
        });
        break;
      case 'turn_started':
        setApproval(null);
        dispatch({
          type: 'turn_started',
          messageId: event.messageId,
          agentId: event.agentId,
          agentName: event.agentName,
          voiceTurnKey: event.voiceTurnKey,
          trigger: event.trigger,
          triggeredByAgentName: event.triggeredByAgentName,
        });
        break;
      case 'thinking_delta':
        dispatch({
          type: 'thinking_delta',
          messageId: event.messageId,
          agentId: event.agentId,
          delta: event.delta,
        });
        break;
      case 'message_delta':
        dispatch({
          type: 'message_delta',
          messageId: event.messageId,
          agentId: event.agentId,
          delta: event.delta,
        });
        break;
      case 'tool_activity':
        dispatch({
          type: 'tool_activity',
          messageId: event.messageId,
          agentId: event.agentId,
          functionName: event.functionName,
          status: event.status,
          toolCallId: event.toolCallId,
          input: event.input,
          output: event.output,
          summary: event.summary,
          changeReceipts: event.changeReceipts,
        });
        break;
      case 'message_complete':
        dispatch({
          type: 'message_complete',
          messageId: event.messageId,
          agentId: event.agentId,
          agentName: event.agentName,
          assistantMessageId: event.assistantMessageId,
          content: event.content,
          thoughts: event.thoughts,
          interaction: event.interaction ?? null,
          tokensSent: event.tokensSent,
          tokensReceived: event.tokensReceived,
          costUsd: event.costUsd ?? null,
          mentions: event.mentions,
          suppressedMentions: event.suppressedMentions,
          voiceTurnKey: event.voiceTurnKey,
          changeReceipts: event.changeReceipts,
        });
        break;
      case 'approval_required':
        setApproval({
          approvalId: event.approvalId,
          exchangeCount: event.exchangeCount,
          pendingAgentNames: event.pendingAgentNames || [],
        });
        break;
      case 'attachment_saved':
        setPendingAttachments((attachments) =>
          attachments.map((attachment) =>
            attachment.clientId === event.clientAttachmentId
              ? {
                  ...attachment,
                  status: 'ready',
                  attachmentId: event.attachmentId,
                }
              : attachment,
          ),
        );
        break;
      case 'attachment_error':
        setPendingAttachments((attachments) =>
          attachments.map((attachment) =>
            attachment.clientId === event.clientAttachmentId
              ? { ...attachment, status: 'error', error: event.message }
              : attachment,
          ),
        );
        break;
      case 'error':
        if (event.messageId !== undefined) {
          dispatch({
            type: event.retryPending ? 'turn_retry_pending' : 'turn_error',
            messageId: event.messageId,
            message: event.message,
          });
        } else {
          setGlobalError(event.message);
          rejectPendingEnsureConversation(ensureConversationRef, event.message);
          dispatch({ type: 'reject_sending' });
        }
        break;
      case 'paused':
        // Not an error: the turn stopped because work is paused. Show a
        // subdued notice instead of a red error (the "⏸️ … paused" system
        // message carries the same explanation and links).
        if (event.messageId !== undefined) {
          dispatch({
            type: 'turn_paused',
            messageId: event.messageId,
            message: event.message,
          });
        }
        break;
      case 'form_patch':
        // The form-assist agent set fields of the form beside this chat;
        // hand them to the owning component (which patches its draft).
        optionsRef.current?.onFormPatch?.({
          formType: event.formType,
          fields: event.fields || {},
        });
        break;
      default:
        break;
    }
  }, []);

  /** Open (or re-open) this team's chat channel on the shared socket */
  const openChannel = useCallback(() => {
    if (!teamId) return;
    rejectPendingEnsureConversation(
      ensureConversationRef,
      'Chat channel reset before it was saved.',
    );
    const requestId = genId();
    requestIdRef.current = requestId;
    channelOpenRef.current = false;
    setConnectionState('connecting');
    const { conversationId, forceNew, wikiScope } = desiredRef.current;
    const opened = send({
      ch: 'chat',
      op: 'open',
      teamId,
      requestId,
      conversationId: forceNew ? undefined : (conversationId ?? undefined),
      new: forceNew || undefined,
      formAssist: optionsRef.current?.formAssist,
      wikiProjectId: forceNew ? wikiScope?.projectId : undefined,
      wikiPagePath: forceNew ? wikiScope?.pagePath : undefined,
    });
    // Consume the one-shot "force new"; a reconnect resumes the resulting
    // conversation rather than forking another
    if (opened) desiredRef.current.forceNew = false;
  }, [teamId, send]);

  // Route this channel's frames (tagged with our requestId) into the
  // existing reducer via handleServerEvent
  useEffect(() => {
    const unsubscribe = subscribe((frame) => {
      if (frame.ch !== 'chat' || frame.requestId !== requestIdRef.current) {
        return;
      }
      if (frame.op === 'error') {
        setGlobalError(frame.message || 'Chat error');
        setConnectionState('disconnected');
        rejectPendingEnsureConversation(
          ensureConversationRef,
          frame.message || 'Chat error',
        );
        return;
      }
      if (frame.op === 'opened') {
        // The channel is now open — sends are allowed from here even if
        // this is a lazy channel with no row yet (conversationId: null).
        channelOpenRef.current = true;
        if (typeof frame.conversationId === 'number') {
          conversationIdRef.current = frame.conversationId;
          desiredRef.current.conversationId = frame.conversationId;
        }
        return;
      }
      if (frame.event) {
        try {
          handleServerEvent(frame.event);
        } catch (error) {
          console.error('Invalid chat event:', error);
        }
      }
    });
    return unsubscribe;
  }, [subscribe, handleServerEvent]);

  // (Re)open the channel whenever the shared socket (re)connects
  useEffect(() => {
    if (!connected) {
      channelOpenRef.current = false;
      rejectPendingEnsureConversation(
        ensureConversationRef,
        'Chat connection lost before conversation was saved.',
      );
      setConnectionState('reconnecting');
      return;
    }
    setGlobalError(null);
    openChannel();
    return () => {
      // Only close a channel that actually completed its open handshake —
      // closing one that never got an 'opened' ack could race the server's
      // own open/close handling for it.
      if (!channelOpenRef.current) return;
      send({
        ch: 'chat',
        op: 'close',
        requestId: requestIdRef.current ?? undefined,
        conversationId: conversationIdRef.current ?? undefined,
      });
    };
    // epoch changes on each (re)connect; teamId change re-opens too
  }, [connected, epoch, teamId, openChannel, send]);

  useEffect(() => {
    const current = conversationIdRef.current;
    if (
      !connected ||
      // This effect's deps include `connected`, so it re-runs on every
      // reconnect — in the same flush as the channel-reopen effect, which
      // runs first and synchronously resets channelOpenRef + mints a new
      // requestId via openChannel() (conversationIdRef survives). Without
      // this guard, drain_pending would be sent before the reopened
      // channel's 'opened' ack — the open/event race the protocol forbids.
      !channelOpenRef.current ||
      !current ||
      approvalEpoch === 0 ||
      !approval?.approvalId ||
      changedApprovalId !== approval.approvalId
    ) {
      return;
    }
    setApproval(null);
    if (
      !send({
        ch: 'chat',
        op: 'event',
        conversationId: current,
        requestId: requestIdRef.current ?? undefined,
        event: { type: 'drain_pending' },
      })
    ) {
      setGlobalError('Not connected. Reconnecting…');
    }
  }, [approval?.approvalId, approvalEpoch, changedApprovalId, connected, send]);

  const newClientId = genId;

  /**
   * Send a chat client-event on this conversation's channel. Frames always
   * carry requestId (the provisional key the server uses to route while a
   * channel is unsaved); conversationId is included once it exists. Gated
   * on channelOpenRef, not conversationIdRef, so the first message can be
   * sent on a lazy channel before the server has created its row.
   */
  const sendChat = useCallback(
    (event: Record<string, unknown>): boolean => {
      const conversationId = conversationIdRef.current;
      const requestId = requestIdRef.current;
      if (!channelOpenRef.current || !requestId) return false;
      return send({
        ch: 'chat',
        op: 'event',
        conversationId: conversationId ?? undefined,
        requestId,
        event,
      });
    },
    [send],
  );

  const ensureConversationId = useCallback((): Promise<number> => {
    if (conversationIdRef.current != null) {
      return Promise.resolve(conversationIdRef.current);
    }
    if (ensureConversationRef.current) {
      return ensureConversationRef.current.promise;
    }
    let resolve!: (conversationId: number) => void;
    let reject!: (error: Error) => void;
    const promise = new Promise<number>((resolvePromise, rejectPromise) => {
      resolve = resolvePromise;
      reject = rejectPromise;
    });
    ensureConversationRef.current = { promise, resolve, reject };
    if (!sendChat({ type: 'ensure_conversation' })) {
      ensureConversationRef.current = null;
      const error = new Error('Not connected. Reconnecting…');
      reject(error);
      setGlobalError(error.message);
    }
    return promise;
  }, [sendChat]);

  const uploadAttachment = useCallback(
    (file: File) => {
      if (!connected || !channelOpenRef.current) {
        setGlobalError('Not connected. Reconnecting…');
        return;
      }

      const clientId = newClientId();
      setPendingAttachments((attachments) => [
        ...attachments,
        {
          clientId,
          filename: file.name,
          mimeType: file.type,
          status: 'uploading',
        },
      ]);

      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        const dataBase64 = result.substring(result.indexOf(',') + 1);
        sendChat({
          type: 'upload_attachment',
          clientAttachmentId: clientId,
          filename: file.name,
          mimeType: file.type,
          dataBase64,
        });
      };
      reader.onerror = () => {
        setPendingAttachments((attachments) =>
          attachments.map((attachment) =>
            attachment.clientId === clientId
              ? { ...attachment, status: 'error', error: 'Could not read file' }
              : attachment,
          ),
        );
      };
      reader.readAsDataURL(file);
    },
    [connected, sendChat],
  );

  const removeAttachment = useCallback((clientId: string) => {
    setPendingAttachments((attachments) =>
      attachments.filter((attachment) => attachment.clientId !== clientId),
    );
  }, []);

  const sendMessage = useCallback(
    (text: string, options?: { targetAgentId?: number }) => {
      if (!connected || !channelOpenRef.current) {
        setGlobalError('Not connected. Reconnecting…');
        return false;
      }

      const readyAttachments = pendingAttachments.filter(
        (attachment) => attachment.status === 'ready',
      );

      const clientMessageId = newClientId();

      if (
        !sendChat({
          type: 'user_message',
          clientMessageId,
          text,
          ...(options?.targetAgentId === undefined
            ? {}
            : { targetAgentId: options.targetAgentId }),
          attachmentIds:
            readyAttachments.length > 0
              ? readyAttachments.map((attachment) => attachment.attachmentId)
              : undefined,
        })
      ) {
        setGlobalError('Not connected. Reconnecting…');
        return false;
      }

      setApproval(null);
      dispatch({
        type: 'add_item',
        item: {
          key: clientMessageId,
          kind: 'user',
          userText: stripFormStateBlock(text),
          authorUserId: user?.id,
          timestamp: new Date().toISOString(),
          attachments:
            readyAttachments.length > 0
              ? readyAttachments.map((attachment) => ({
                  filename: attachment.filename,
                  mimeType: attachment.mimeType,
                  attachmentId: attachment.attachmentId,
                }))
              : undefined,
          sendStatus: 'sending',
          thinking: '',
          toolActivity: [],
          segments: [],
          streamingContent: '',
        },
      });
      setPendingAttachments([]);
      return true;
    },
    [connected, pendingAttachments, sendChat, user?.id],
  );

  /** Stop the current response; pending messages continue immediately */
  const stopProcessing = useCallback(() => {
    setApproval(null);
    sendChat({ type: 'stop' });
  }, [sendChat]);

  /** Approve continued agent-to-agent exchanges (resets the guard) */
  const approveAgents = useCallback(() => {
    setApproval(null);
    if (!sendChat({ type: 'approve_agents' })) {
      setGlobalError('Not connected. Reconnecting…');
    }
  }, [sendChat]);

  /** Switch this channel to a specific conversation, or a brand-new one */
  const switchConversation = useCallback(
    (target: {
      conversationId: number | null;
      forceNew: boolean;
      wikiScope?: { projectId: number; pagePath: string };
    }) => {
      rejectPendingEnsureConversation(
        ensureConversationRef,
        'Conversation changed before it was saved.',
      );
      // Only close the outgoing channel if it actually finished opening —
      // never close one whose 'opened' ack hasn't arrived yet.
      if (channelOpenRef.current) {
        send({
          ch: 'chat',
          op: 'close',
          requestId: requestIdRef.current ?? undefined,
          conversationId: conversationIdRef.current ?? undefined,
        });
      }
      channelOpenRef.current = false;
      conversationIdRef.current = target.conversationId;
      desiredRef.current = target;
      setConversationGeneration((generation) => generation + 1);
      setPendingAttachments([]);
      setApproval(null);
      dispatch({ type: 'reset', items: [] });
      openChannel();
    },
    [send, openChannel],
  );

  const startNewConversation = useCallback(
    (options?: { wikiScope?: { projectId: number; pagePath: string } }) => {
      switchConversation({
        conversationId: null,
        forceNew: true,
        wikiScope: options?.wikiScope,
      });
    },
    [switchConversation],
  );

  const selectConversation = useCallback(
    (id: number) => {
      switchConversation({ conversationId: id, forceNew: false });
    },
    [switchConversation],
  );

  const isProcessing = items.some(
    (item) =>
      (item.kind === 'agent' && item.status === 'processing') ||
      (item.kind === 'user' && item.sendStatus === 'sending'),
  );

  return {
    items,
    agents,
    participants,
    approval,
    connectionState,
    conversationId,
    conversationGeneration,
    conversationTitle,
    globalError,
    sendMessage,
    ensureConversationId,
    approveAgents,
    stopProcessing,
    isProcessing,
    pendingAttachments,
    uploadAttachment,
    removeAttachment,
    startNewConversation,
    selectConversation,
    clearGlobalError: () => setGlobalError(null),
  };
}
